import { useTracking } from 'react-tracking';
import { push } from 'redux-first-router';

import { useMount } from '@simplywallst/ui-core';

import { ROUTE_PUBLIC_NARRATIVE_EDIT } from '@/constants/routes';
import { getPath } from '@/router/utils';

import useUser from '@/hooks/useUser';

import { useCreatePublicNarrative } from '../../hooks/useCreatePublicNarrative';
import type { NarrativeForm } from '../../utils/schema';
import { NarrativeFormContent } from '../NarrativeFormContent';
import {
  narrativeGetValuatorQueryParamInitialValues,
  transformFormValuesToValuation,
} from '../../utils/transformation';
import { useNarrativeBack } from '../../hooks/useNarrativeBack';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getTradingItemCurrencyISO } from '@/utilities/getTradingItemCurrencyISO';
import loadable from '@loadable/component';
import { merge } from 'lodash';
import { useNarrativeInvalidate } from '../../hooks/useNarrativeInvalidate';
import { useNarrativeWatchCompanyContext } from '../NarrativeWatchCompany';
import { useNarrativeValuationManager } from '../../hooks/useNarrativeValuationFormManager/useNarrativeValuationFormManager';
import { useFlag } from '@simplywallst/unleash';
import { useHasSavedContent } from '../../hooks/useHasSavedContent';
import { useNarrativeInputPayload } from '../../hooks/useNarrativeInputPayload';
import { useUpdateNarrativeUpdateWithSnackbar } from '../../hooks/useUpdateNarrativeUpdate/useUpdateNarrativeUpdate';
import { usePublishNarrativeUpdateWithSnackbar } from '../../hooks/usePublishNarrative/usePublishNarrative';
import { useNarrativeFormManager } from '../../hooks/useNarrativeFormManager';
import { hasErrors } from '../../hooks/useNarrativeForm';
import { useStartNarrativeWithSnackbar } from '../../hooks/useStartNarrative/useStartNarrative';
import { useUserProfile } from '@/hooks/useUserProfile';
import { calculateValuationInitialisation } from '../Calculators/calculateInitialisation';
import type { MyNarrativeCompany } from '../../hooks/useNarrativeCompany';

export type Props = {
  company: MyNarrativeCompany['Company'];
  initialUserHoldsPosition: boolean;
};

const NarrativeOnboardingModal = loadable(
  () => import('@components/NarrativeOnboardingModal')
);

export const PublicNarrativeCreate = ({
  company,
  initialUserHoldsPosition,
}: Props) => {
  const { data: user } = useUser();
  const { communityProfile } = useUserProfile();
  const [showingOnboarding, setShowingOnboarding] = useState(
    !Boolean(user?.displayName)
  );

  const { trackEvent, Track } = useTracking({
    type: 'track',
    subject: 'public-narrative',
    userId: user?.id,
    companyId: company.id,
  });
  const { returnPath, goBack } = useNarrativeBack();
  const { invalidate } = useNarrativeInvalidate();
  const { open } = useNarrativeWatchCompanyContext();

  const {
    enabled: updateNarrativeUpdateEnabled,
    ready: updateNarrativeUpdateReady,
  } = useFlag('narrative-content-from-updates');

  const isUpdateNarrativeUpdateEnabled =
    updateNarrativeUpdateEnabled && updateNarrativeUpdateReady;

  const goBackWithSuccess = useCallback(
    (narrativeId: string) => {
      const path = getPath({
        type: ROUTE_PUBLIC_NARRATIVE_EDIT,
        payload: { id: narrativeId },
        query: returnPath
          ? {
              company: company.id,
              returnPath,
            }
          : {},
      });

      invalidate();

      trackEvent({
        action: isUpdateNarrativeUpdateEnabled ? 'save' : 'publish',
      });

      push(path);
      goBack();
      open({ companyId: company.id });
    },
    [
      returnPath,
      company.id,
      invalidate,
      isUpdateNarrativeUpdateEnabled,
      trackEvent,
      goBack,
      open,
    ]
  );

  const { mutate: createNarrative, status: createNarrativeStatus } =
    useCreatePublicNarrative({
      onSuccess: (narrativeId) => {
        goBackWithSuccess(narrativeId);
      },
    });

  useMount(() => {
    trackEvent({ action: 'landed' });
  });

  useEffect(() => {
    if (showingOnboarding) {
      trackEvent({ action: 'onboarding' });
    }
  }, [showingOnboarding, trackEvent]);

  useEffect(() => {
    if (!showingOnboarding && !user?.displayName) {
      goBack();
    }
  }, [showingOnboarding, user?.displayName, goBack]);

  const [successSnackbar, setSuccessSnackbar] = useState(false);

  const [narrativeUpdateId, setNarrativeUpdateId] = useState<string>(
    company.myNarrative?.draftUpdate?.id ?? ''
  );

  const [narrativeId, setNarrativeId] = useState<string>(
    company.myNarrative?.id ?? ''
  );

  const { hasSavedContent, setHasSavedContent } = useHasSavedContent();

  const submitLabel = isUpdateNarrativeUpdateEnabled ? 'Publish' : 'Save';
  const cancelLabel = isUpdateNarrativeUpdateEnabled ? 'Close draft' : 'Cancel';

  const {
    mutate: publishNarrativeUpdateAsync,
    status: publishNarrativeUpdateStatus,
  } = usePublishNarrativeUpdateWithSnackbar({
    includeSuccessSnackbar: successSnackbar,
  });

  const { mutate: startNarrativeAsync } = useStartNarrativeWithSnackbar({
    includeSuccessSnackbar: successSnackbar,
  });

  const {
    calculateFairValue,
    calculateFairValueFromInitialisation,
    calculateRevenueGrowthRateFromFairValue,
    calculateBreakDown,
    pastAnnualRevenueEarnings,
    valuation,
  } = useNarrativeValuationManager({
    company,
    isUpdateNarrativeUpdateEnabled,
    draftUpdate: company.myNarrative?.draftUpdate,
    latestPublishedUpdate: company.myNarrative?.latestPublishedUpdate,
  });

  const initialNarrativeForm: NarrativeForm = useMemo(() => {
    const calculatorOutputs = valuation
      ? calculateValuationInitialisation({
          valuation,
          calculateFairValueFromInitialisation,
          calculateRevenueGrowthRateFromFairValue,
        })
      : calculateFairValueFromInitialisation();

    return merge(
      {
        ...calculatorOutputs,
        userHoldsPosition: initialUserHoldsPosition,
        title: company.myNarrative?.draftUpdate?.title ?? '',
        content: {
          text: company.myNarrative?.draftUpdate?.contentHtml ?? '',
          html: company.myNarrative?.draftUpdate?.contentHtml ?? '',
        },
      },
      narrativeGetValuatorQueryParamInitialValues()
    );
  }, [
    valuation,
    calculateFairValueFromInitialisation,
    calculateRevenueGrowthRateFromFairValue,
    initialUserHoldsPosition,
    company.myNarrative?.draftUpdate?.title,
    company.myNarrative?.draftUpdate?.contentHtml,
  ]);

  const {
    values,
    errors,
    setSubmitCount,
    validateForm,
    resetForm,
    handleContentChange,
    handleHeaderChange,
    handleSetFairValue,
    handleSetPartialValues,
  } = useNarrativeFormManager({
    initialNarrativeForm,
    calculateFairValue,
    calculateRevenueGrowthRateFromFairValue,
    trackEvent,
    isUpdateNarrativeUpdateEnabled,
    setHasSavedContent,
    narrativeUpdateId,
    narrativeId,
    companyId: company.id,
    successSnackbar,
    setNarrativeUpdateId,
    setNarrativeId,
  });

  useEffect(() => {
    setNarrativeId(company.myNarrative?.id ?? '');
    if (!company.myNarrative) {
      resetForm();
    }
  }, [company.myNarrative, resetForm]);

  const narrativeInputPayload = useNarrativeInputPayload({
    values,
    narrativeId,
    companyId: company.id,
  });

  const { mutate: updateNarrativeUpdateAsync } =
    useUpdateNarrativeUpdateWithSnackbar({
      includeSuccessSnackbar: successSnackbar,
    });

  const handleSubmit = async () => {
    setSubmitCount((c) => c + 1);

    if (hasErrors(validateForm())) return;

    if (isUpdateNarrativeUpdateEnabled && narrativeUpdateId) {
      await updateNarrativeUpdateAsync({
        id: narrativeUpdateId,
        input: {
          contentHtml: values.content.html,
          title: values.title,
          valuation: {
            ...transformFormValuesToValuation({ values }),
          },
        },
      });

      await publishNarrativeUpdateAsync({
        id: narrativeUpdateId,
        input: { userHoldsPosition: values.userHoldsPosition ?? false },
      });

      goBackWithSuccess(narrativeId);
    } else if (isUpdateNarrativeUpdateEnabled) {
      const { startNarrative } = await startNarrativeAsync({
        companyId: company.id,
      });
      setNarrativeId(startNarrative?.id ?? '');
      setNarrativeUpdateId(startNarrative?.draftUpdate?.id ?? '');
    } else {
      if (typeof company !== 'undefined') {
        const currencyIso = getTradingItemCurrencyISO(
          company?.data.currencyInfo
        );
        if (!currencyIso) throw new Error('Currency ISO is missing');

        createNarrative({ input: narrativeInputPayload, setPrimary: true });
        setSubmitCount(0);
      }
    }
  };

  const isLoading = isUpdateNarrativeUpdateEnabled
    ? publishNarrativeUpdateStatus === 'pending'
    : createNarrativeStatus === 'pending';

  const owner = useMemo(
    () => ({
      displayName: communityProfile?.displayName || user?.displayName,
      imageUrl: communityProfile?.imageUrl || '',
      role: 'Community Contributor',
      publicId: communityProfile?.publicId || '',
      disclaimerType: 'COMMUNITY' as const,
    }),
    [
      user?.displayName,
      communityProfile?.displayName,
      communityProfile?.imageUrl,
      communityProfile?.publicId,
    ]
  );

  if (showingOnboarding)
    return (
      <Track>
        <NarrativeOnboardingModal
          companyId={company.id}
          isOpen={showingOnboarding}
          onClose={() => {
            setShowingOnboarding(false);
          }}
        />
      </Track>
    );

  return (
    <Track>
      <NarrativeFormContent
        isPublishedLocked={false}
        initialFairValue={initialNarrativeForm.fairValue}
        errors={errors}
        values={values}
        onChange={handleSetPartialValues}
        onSetFairValue={handleSetFairValue}
        onContentChange={handleContentChange}
        onHeaderChange={handleHeaderChange}
        isLoading={isLoading}
        onSubmit={handleSubmit}
        submitLabel={submitLabel}
        company={company}
        initialContentHtml={initialNarrativeForm.content.html}
        owner={owner}
        onReset={resetForm}
        pastAnnualRevenueEarnings={pastAnnualRevenueEarnings}
        calculationBreakDownOutput={calculateBreakDown({
          discountRatePercentage: values.discountRatePercentage,
          futurePE: values.priceEarningsMultiple,
          profitMarginPercentage: values.profitMarginPercentage,
          revenueGrowthRatePercentage: values.revenuePercentage,
          timeFrame: values.timePeriod,
        })}
        hasSavedContent={hasSavedContent}
        setSuccessSnackbar={setSuccessSnackbar}
        cancelLabel={cancelLabel}
      />
    </Track>
  );
};
